/**
 * Onboarding persistence service — cut 1 + cut 2 + step-3 fix.
 *
 * Builds and posts the onboarding payload to
 * `POST /api/users/onboarding` through the centralized `apiClient`. The
 * service is intentionally narrow: it owns the payload shape, the network
 * call, and the canonical-success validation. It does NOT touch
 * localStorage — the cleanup lifecycle is owned by `usePersistOnboardingData`
 * so the persistence decision lives in a single place.
 *
 * Cut-1 contract (the 10 base fields):
 *   - `user_id`, `city_id`, `city_name`, `location_id`, `location_name`,
 *     `sector_id`, `sector_name`, `purchase_frequency`, `monthly_spend`,
 *     `potential_savings`
 *   - `potential_savings` is recomputed from `monthly_spend` +
 *     `purchase_frequency` via `computeSavings` at save time; it is NEVER
 *     read from localStorage.
 *   - `sector_id` / `sector_name` mirror `location_id` / `location_name`
 *     until the backend cut 2 settles the canonical naming.
 *
 * Step-3 fix (post-cut-2): the payload now also carries `store_ids` and
 * `subscription_ids` so the backend can persist `user_store_preference`
 * and `user_suscriptions` rows for the user.
 *
 *   - `store_ids: number[]` is the list of `StorePreference.id` values
 *     the user kept enabled on the step-3 supermarket card. The frontend
 *     has direct access to numeric store IDs via the
 *     `/settings/store_preferences_public` response.
 *   - `subscription_ids: number[]` is the list of numeric `suscriptions.id`
 *     values the user kept checked on the step-3 membership rows. The
 *     numeric id is what the backend persists into
 *     `user_suscriptions.suscription_id` per the `OnboardingRequest`
 *     Pydantic schema (`List[int]`). The frontend reads the id from
 *     `MemberShips.id` in the public endpoint response.
 *
 * Cut-2 contract (canonical success shape): the backend MUST resolve the
 * POST as `200 { saved: true, onb_completed: true }`. Any other response
 * shape (`null` from a `204 No Content`, `200 { saved: true }` without
 * `onb_completed`, etc.) is non-canonical and the service throws
 * `InvalidOnboardingResponseError`. The hook's `.catch` then retains the
 * draft for the next `/store` visit — the only way the user can retry.
 */

import apiClient from "modules/main/services/apiClient";
import { endPointUserOnboarding } from "modules/main/services/endpoints";
import { computeSavings, getMonthlyMultiplier } from "modules/onboarding/components/step-five";
import type { OnboardingState } from "modules/onboarding/types/onboarding-state";

/**
 * POST payload for `POST /api/users/onboarding`.
 *
 * Field set is the 10 cut-1 keys plus the 2 cut-2 supermarket-selection
 * arrays. The keys mirror the backend contract documented in
 * `app/api/models/onboarding.py::OnboardingRequest`.
 */
export interface SaveUserOnboardingPayload {
  user_id: number;
  city_id: number | null;
  city_name: string | null;
  location_id: number | null;
  location_name: string | null;
  sector_id: number | null;
  sector_name: string | null;
  purchase_frequency: "daily" | "weekly" | "monthly" | "other";
  monthly_spend: number;
  potential_savings: number;
  /** Numeric `StorePreference.id` values the user kept enabled on step 3. */
  store_ids: number[];
  /**
   * Numeric `suscriptions.id` values the user kept checked on step 3.
   * The backend `OnboardingRequest.subscription_ids` field is typed
   * `List[int]`; sending numeric ids verbatim keeps the persistence
   * contract aligned end-to-end (the public
   * `/settings/store_preferences_public` response exposes the numeric
   * id via `MemberShips.id`).
   */
  subscription_ids: number[];
  /**
   * The user's usual/favorite store id (`StorePreference.id`), or `null`
   * when the user has not selected a favorite on step 3. Persisted into
   * `user_onboarding_data.habitual_store_id` by the backend. When `null`,
   * the backend falls back to the first entry in `store_ids` for backward
   * compatibility with older clients that do not send this field.
   */
  habitual_store_id: number | null;
}

/**
 * Canonical success body for `POST /api/users/onboarding` (cut 2).
 *
 * The backend MUST resolve the POST as `200 { saved: true, onb_completed: true }`.
 * Any other shape is non-canonical and the service throws — see
 * `isCanonicalOnboardingResponse` and `InvalidOnboardingResponseError`.
 */
export interface SaveOnboardingResponse {
  saved: boolean;
  onb_completed: boolean;
}

/**
 * Thrown by `saveOnboardingData` when the backend's response is not the
 * canonical `{ saved: true, onb_completed: true }` shape. The hook catches
 * this in its `.catch` branch and retains the local draft so the next
 * `/store` visit can retry. The `received` property carries the raw body
 * (which may be `null`, a primitive, an array, or an object missing one
 * of the two flags) for diagnostics.
 */
export class InvalidOnboardingResponseError extends Error {
  readonly received: unknown;

  constructor(message: string, received: unknown) {
    super(message);
    this.name = "InvalidOnboardingResponseError";
    this.received = received;
  }
}

/**
 * Returns a short, safe textual description of the received value for
 * error messages. The full payload is intentionally NOT embedded in the
 * message — it can be arbitrarily large and may contain PII.
 */
const describeReceived = (value: unknown): string => {
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "undefined";
  }
  if (Array.isArray(value)) {
    return `array(length=${value.length})`;
  }
  const type = typeof value;
  if (type !== "object") {
    return `${type}(${JSON.stringify(value)})`;
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return `object(keys=${keys.join(",") || "<empty>"})`;
};

/**
 * Type guard for the cut-2 canonical success shape. Strict equality
 * (`saved === true && onb_completed === true`) rejects:
 *   - missing keys (`undefined`)
 *   - `false` values
 *   - truthy non-booleans (`"yes"`, `1`, etc.)
 *   - non-object responses (`null`, primitives)
 *   - arrays (typeof null/object ambiguity)
 *
 * Only the exact shape `{ saved: true, onb_completed: true }` passes.
 */
export const isCanonicalOnboardingResponse = (
  value: unknown,
): value is SaveOnboardingResponse =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  (value as Record<string, unknown>).saved === true &&
  (value as Record<string, unknown>).onb_completed === true;

/**
 * Pure builder for the cut-1 payload. Kept separate from the network call
 * so the shape can be unit-tested without mocking `apiClient`.
 *
 * Contract enforced here:
 *   - All ten required keys are present (verified by the type).
 *   - `sector_*` mirrors `location_*` until backend cut 2 settles naming.
 *   - `potential_savings` is derived from effective monthly spend
 *     (`spend_per_purchase * getMonthlyMultiplier(freq)`) through
 *     `computeSavings`, never from the persisted draft.
 *   - `monthly_spend` envía el valor por compra (no mensual) para
 *     mantener consistencia con los registros históricos del backend.
 *
 * @param userId  The authenticated `userData.id`. Callers MUST guarantee
 *                this is a positive integer (the hook guards it).
 * @param draft   The persisted onboarding draft read from localStorage.
 */
export const buildSaveOnboardingPayload = (
  userId: number,
  draft: OnboardingState,
): SaveUserOnboardingPayload => {
  // The hook guards these fields are present before calling the service,
  // but the builder is a public API and must tolerate the typed null
  // contract — assert at runtime so a misuse surfaces a clear error.
  if (draft.spend_per_purchase === null) {
    throw new Error(
      "buildSaveOnboardingPayload requires a non-null spend_per_purchase",
    );
  }
  if (draft.purchase_frequency === null) {
    throw new Error(
      "buildSaveOnboardingPayload requires a non-null purchase_frequency",
    );
  }

  // `potential_savings` se computa desde el mensual efectivo para que
  // refleje el ahorro real. `monthly_spend` mantiene el valor por compra
  // para no romper los registros históricos del backend.
  const effectiveMonthly = draft.spend_per_purchase * getMonthlyMultiplier(draft.purchase_frequency);
  const projection = computeSavings(effectiveMonthly, draft.purchase_frequency);

  return {
    user_id: userId,
    city_id: draft.city_id,
    city_name: draft.city_name,
    location_id: draft.location_id,
    location_name: draft.location_name,
    sector_id: draft.location_id,
    sector_name: draft.location_name,
    purchase_frequency: draft.purchase_frequency,
    monthly_spend: draft.spend_per_purchase,
    potential_savings: projection.annualSavings,
    store_ids: draft.store_ids,
    subscription_ids: draft.subscription_ids,
    habitual_store_id: draft.habitual_store_id,
  };
};

/**
 * POSTs the cut-1 onboarding payload to the backend via `apiClient` and
 * validates the response against the cut-2 canonical success shape.
 *
 * Resolves with the canonical response on success:
 *   `{ saved: true, onb_completed: true }`
 *
 * Rejects with `InvalidOnboardingResponseError` on ANY non-canonical
 * response (`null`, `204 No Content`, `{ saved: true }` without
 * `onb_completed`, `{ saved: false, ... }`, primitives, arrays, etc.).
 * This routes the rejection into the hook's `.catch` branch, which
 * retains the local draft so the next `/store` visit can retry.
 *
 * Rejects with the raw network/HTTP error (rethrown by `apiClient`) on
 * any non-2xx or transport failure. The hook treats this the same way —
 * the draft is preserved.
 *
 * Never reads or writes localStorage; the hook owns the draft cleanup
 * lifecycle.
 */
export const saveOnboardingData = async (
  userId: number,
  draft: OnboardingState,
): Promise<SaveOnboardingResponse> => {
  const payload = buildSaveOnboardingPayload(userId, draft);
  const response = await apiClient.post<unknown>(endPointUserOnboarding, payload);

  if (!isCanonicalOnboardingResponse(response)) {
    throw new InvalidOnboardingResponseError(
      `saveOnboardingData received a non-canonical onboarding response from the backend (received: ${describeReceived(response)}). Draft was preserved for retry on the next /store visit.`,
      response,
    );
  }

  return response;
};